import React, { useCallback, useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import HandWave from '../../../assets/images/handwave.svg?component';
import { useIsMobile, usePointerActivation } from '@/hooks';
import { cn } from '@/utils';

export default function HiatusModal({ open, onClose, playerName, autoCloseMs = 5000 }) {
	const cardRef = useRef(null);
	const [visible, setVisible] = useState(false);
	const timerRef = useRef(null);
	const isMobile = useIsMobile();
	const closeNow = useCallback(
		(reason) => {
			setVisible(false);
			setTimeout(() => onClose?.(reason || 'close'), 150);
		},
		[onClose]
	);
	const continuePointerProps = usePointerActivation(() => closeNow('continue'));

	useEffect(() => {
		if (!open) return;

		setVisible(true);

		const prev = document.body.style.overflow;
		document.body.style.overflow = 'hidden';

		const onKey = (e) => {
			if (e.key === 'Escape') onClose?.('escape');
		};
		window.addEventListener('keydown', onKey);

		if (autoCloseMs > 0) {
			timerRef.current = setTimeout(() => onClose?.('timeout'), autoCloseMs);
		}

		return () => {
			document.body.style.overflow = prev;
			window.removeEventListener('keydown', onKey);
			if (timerRef.current) clearTimeout(timerRef.current);
		};
	}, [open, autoCloseMs, onClose]);

	if (!open) return null;

	function truncate(text, maxLength) {
		return text.length > maxLength ? text.slice(0, maxLength) + '...' : text;
	}

	return (
		<div
			className="fixed inset-0 z-9999 flex items-center justify-center"
			role="dialog"
			aria-modal="true"
		>
			{/* Backdrop */}
			<button
				aria-label="Close"
				onClick={() => closeNow('backdrop')}
				className="absolute inset-0 bg-black/60"
			/>

			{/* Card */}
			<div
				ref={cardRef}
				className={cn(
					`relative mx-4 w-full rounded-lg border border-[#2D3A6C]
                  bg-[#1E2748] p-4 sm:p-12 text-center shadow-2xl
                  transition-all duration-200 ease-out
                  ${visible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`,
					isMobile && 'max-w-[345px] h-[446px]',
					!isMobile && 'max-w-[458px] h-[462px]'
				)}
			>
				{/* X button */}
				<button
					onClick={() => closeNow('x')}
					aria-label="Close"
					className="absolute right-2 top-2 rounded-full p-1 text-[#D4D5D8]"
				>
					<X className="h-6 w-6" />
				</button>

				<div className="relative mx-auto mb-4 mt-6 h-40 w-40 flex items-center justify-center">
					<img
						src={HandWave}
						className="h-[160px] w-[160px] object-contain rounded-full"
						onError={(e) => {
							e.currentTarget.style.display = 'none';
						}}
					/>
				</div>

				{/* Headline */}
				<h2 className="text-[#F7F7F8] text-3xl sm:text-4xl font-bold leading-9">
					Welcome Back,
					<br /> {truncate(playerName, 13)}
				</h2>

				{/* Subhead */}
				<p className="mt-3 mb-8 text-[#D4D5D8] text-sm font-normal">
					Look who’s back! The markets missed you.
				</p>

				{/* CTA */}
				<div className="mt-2">
					<button
						{...continuePointerProps}
						className="inline-flex items-center justify-center rounded-[30px] px-10 py-3 font-bold text-[#F7F7F8]
                       bg-blue-custom-2 hover:opacity-95 active:opacity-90 outline outline-1 -outline-offset-1 outline-blue-custom-2"
					>
						Get back in the game
					</button>
				</div>
			</div>
		</div>
	);
}
